import { Lead, Question } from '.';
import { IAnswerInfoDb, IFetchedAnswer } from 'common/interfaces/answer';
import { IQuestionWithOptionInfo } from 'common/interfaces/question';
import debounce from 'lodash-es/debounce';
import { action, computed, observable, reaction } from 'mobx';
import { app } from './../App';

// Store
export class Answer {
  constructor(private lead: Lead, private question: Question) {
    this.pushAnswers = debounce(this.pushAnswers, 2000);
  }

  private fetchNewLead = reaction(
    () => this.lead.openLeadId,
    (openLeadId: number) => {
      if (openLeadId !== null) {
        this.fetchAnswers();
      }
    }
  );

  @observable public loading: boolean = false;

  @observable public answers: IFetchedAnswer[] = [];

  @computed public get answersQuestionsIds(): number[] {
    return this.answers.map((answer, i) => answer.id);
  }

  @computed public get isAnswerInfoFetched(): boolean {
    return this.loading === false && this.answers.length > 0;
  }

  // Find ID of the question that contains wholesaler info and their sourcing programs
  @computed
  private get primaryWholesalerSourcingProgramsQuestion(): IQuestionWithOptionInfo | null {
    try {
      let foundQuestion: IQuestionWithOptionInfo = null;

      this.question.questionWithOptionInfo.some((question, i) => {
        if (question.optionTable === 'wholesaler') {
          foundQuestion = question;
          return true;
        }
      });

      return foundQuestion;
    } catch (err) {
      return null;
    }
  }

  // Find existing answer, if there is any, for primary wholesaler
  @computed
  private get primaryWholesalerQuestionAnswer(): IFetchedAnswer | null {
    try {
      let foundAnswer: IFetchedAnswer = null;

      this.answers.some((answer, i) => {
        if (answer.id === this.primaryWholesalerSourcingProgramsQuestion.id) {
          foundAnswer = answer;
          return true;
        }
      });

      return foundAnswer;
    } catch (err) {
      return null;
    }
  }

  // Find sourcing program if primary wholesaler have been picked
  @computed public get primaryWholesalerSourcingProgramsIds(): number[] | null {
    try {
      const primaryWholesalerId: number =
        typeof this.primaryWholesalerQuestionAnswer.answer[0].id !== 'undefined'
          ? this.primaryWholesalerQuestionAnswer.answer[0].id
          : this.primaryWholesalerQuestionAnswer.answer[0];

      let sourcingProgramsIds: number[] = [];

      this.primaryWholesalerSourcingProgramsQuestion.option.some(
        (wholesaler, i) => {
          if (wholesaler.id === primaryWholesalerId) {
            sourcingProgramsIds = wholesaler.sourcingProgramIds;
            return true;
          }
        }
      );

      return sourcingProgramsIds;
    } catch (err) {
      return null;
    }
  }

  // Fetch all questions
  @action public fetchAnswers = async (
    hideLoader?: boolean
  ): Promise<IAnswerInfoDb> => {
    try {
      const id = this.lead.openLeadId;

      if (typeof hideLoader === 'undefined' || hideLoader === false) {
        this.loading = true;
      }

      const questionAnswerService = await app.service('question-answer');
      let answerInfo: IAnswerInfoDb = await questionAnswerService.get(id);

      if (answerInfo === null) {
        const answerService = await app.service('answer');
        const initializeAnswer = await answerService.create({
          providerId: id,
        });

        answerInfo = await questionAnswerService.get(id);
      }

      this.answers = answerInfo.selectedAnswer.map((answer, i) => {
        return {
          ...answer,
          note:
            typeof answer.note !== 'undefined' && answer.note !== null
              ? answer.note
              : null,
          others:
            typeof answer.others !== 'undefined' && answer.others !== null
              ? answer.others
              : null,
          answer:
            typeof answer.answer !== 'undefined' &&
            answer.answer &&
            answer.answer.length > 0
              ? answer.answer
              : [],
          saving: false,
        };
      });

      return answerInfo;
    } catch (err) {
      this.answers = [];
      console.error(err);
      return err;
    } finally {
      this.loading = false;
    }
  };

  @action public changeAnswerNote = (
    answerId: number,
    question: IQuestionWithOptionInfo,
    noteText: string,
    noteHtml: string
  ) => {
    const answerIndex = this.answersQuestionsIds.indexOf(question.id);
    if (answerIndex !== -1) {
      this.answers[answerIndex].saving = true;
      this.answers[answerIndex].note = noteText;

      this.pushAnswers();
    }
  };

  @action public changeAnswerValue = (
    answerId: number,
    question: IQuestionWithOptionInfo,
    value: any,
    label: string
  ) => {
    const answerIndex = this.answersQuestionsIds.indexOf(question.id);
    if (answerIndex !== -1) {
      this.answers[answerIndex].saving = true;
      this.answers[answerIndex].answer = [
        {
          id: value,
          name: label,
        },
      ];

      this.pushAnswers();
    }
  };

  @action public changeAnswerCheckbox = (
    answerId: number,
    question: IQuestionWithOptionInfo,
    checked: boolean,
    value: any
  ) => {
    const answerIndex = this.answersQuestionsIds.indexOf(question.id);
    if (answerIndex !== -1) {
      this.answers[answerIndex].saving = true;

      let answersArray =
        this.answers[answerIndex].answer !== null &&
        this.answers[answerIndex].answer.length > 0
          ? typeof this.answers[answerIndex].answer[0] === 'object' &&
            typeof this.answers[answerIndex].answer[0].id !== 'undefined'
            ? this.answers[answerIndex].answer.map((answer, i) => answer.id)
            : this.answers[answerIndex].answer
          : [];

      const valueIndex = answersArray.indexOf(value);
      if (checked) {
        if (valueIndex === -1) {
          answersArray.push(value);
        }
      } else {
        if (valueIndex !== -1) {
          answersArray = answersArray
            .slice(0, valueIndex)
            .concat(answersArray.slice(valueIndex + 1));
        }
      }

      this.answers[answerIndex].answer = answersArray;

      this.pushAnswers();
    }
  };

  @action public changeAnswerSelect = (
    answerId: number,
    question: IQuestionWithOptionInfo,
    value: any
  ) => {
    const answerIndex = this.answersQuestionsIds.indexOf(question.id);
    if (answerIndex !== -1) {
      this.answers[answerIndex].saving = true;
      this.answers[answerIndex].answer = [value];

      this.pushAnswers();
    }
  };

  @action public updateAnswers = async (): Promise<any> => {
    try {
      const questionAnswerService: any = app.service('question-answer');

      const answers = this.answers.map((answer, i) => {
        return {
          questionId: answer.id,
          others:
            typeof answer.others !== 'undefined' && answer.others !== null
              ? answer.others
              : null,
          note:
            typeof answer.note !== 'undefined' && answer.note !== null
              ? answer.note.trim()
              : null,
          answer:
            answer.answer !== null && answer.answer.length > 0
              ? typeof answer.answer[0] === 'object' &&
                typeof answer.answer[0].id !== 'undefined'
                ? answer.answer.map((ans) => ans.id)
                : answer.answer
              : [],
        };
      });

      const updated = await questionAnswerService.update(
        this.lead.openLeadId,
        answers
      );

      return updated;
    } catch (err) {
      console.error(err);
      return err;
    }
  };

  @action public pushAnswers = async (): Promise<any> => {
    try {
      if (this.answers.length > 0) {
        await this.updateAnswers();
      }

      this.answers.map((answer, i) => {
        this.answers[i].saving = false;
      });

      return true;
    } catch (err) {
      console.error(err);
      return err;
    }
  };
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./app/web/src/stores/Answer.ts